const API_BASE = "/api";

export type ModelingStatus = "queued" | "in_progress" | "done";

export interface Branding {
  icon_url?: string | null;
  icon_path?: string | null;
  logo_url?: string | null;
  logo_path?: string | null;
}

export interface FirmSummary {
  slug: string;
  name: string;
  ticker: string | null;
  f500_rank: number | null;
  revenue_b_usd_fy2025: number | null;
  sector: string | null;
  industry: string | null;
  hq: string | null;
  employees: number | null;
  pitch: string | null;
  modeling_status: ModelingStatus;
  priority_order: number | null;
  market_cap?: number | null;
  primary_exchange?: string | null;
  branding?: Branding | null;
}

export interface FirmFramework {
  clearing_layer: string;
  verification: string;
  surviving_moats: string;
  compression_risk: string;
  fork: string;
  verdict: string;
}

export interface FinancialsIncome {
  revenue: number | null;
  cost_of_revenue: number | null;
  gross_profit: number | null;
  operating_income: number | null;
  net_income: number | null;
  ebitda: number | null;
  diluted_eps: number | null;
  sga: number | null;
  rd: number | null;
}

export interface FinancialsCashFlow {
  operating_cf: number | null;
  capex: number | null;
  free_cf: number | null;
  dividends: number | null;
}

export interface FinancialsBalance {
  total_assets: number | null;
  total_liabilities: number | null;
  total_equity: number | null;
  cash: number | null;
  inventories: number | null;
  ppe_net: number | null;
  long_term_debt: number | null;
  goodwill: number | null;
}

export interface FinancialsTTM {
  period_end: string;
  revenue: number | null;
  operating_income: number | null;
  net_income: number | null;
  ebitda: number | null;
}

export interface FinancialsSource {
  api: string;
  endpoint_base: string;
  pulled_at: string;
}

export interface Financials {
  ticker: string;
  cik: string;
  as_of: string;
  fiscal_year: number;
  filing_date: string;
  annual: {
    income: FinancialsIncome;
    cash_flow: FinancialsCashFlow;
    balance: FinancialsBalance;
  };
  ttm: FinancialsTTM | null;
  source: FinancialsSource;
}

export interface OperationsScaleItem {
  label: string;
  value: string;
}

export interface OperationsRhythmItem {
  cadence: string;
  what: string;
}

export interface Operations {
  scale: OperationsScaleItem[];
  rhythm: OperationsRhythmItem[];
  core_processes: string[];
  failure_modes: string[];
}

export interface AiPosture {
  what_survives: string;
  what_compresses: string;
  timeline: string;
  signal: string;
}

export interface OverviewSource {
  api: string;
  endpoint: string;
  pulled_at: string;
}

export interface Firm extends FirmSummary {
  framework: FirmFramework;
  framework_source: string;
  model_path: string | null;
  dashboard_path: string | null;
  financials?: Financials | null;
  operations?: Operations | null;
  ai_posture?: AiPosture | null;
  legal_name?: string | null;
  description?: string | null;
  homepage_url?: string | null;
  phone_number?: string | null;
  list_date?: string | null;
  cik?: string | null;
  composite_figi?: string | null;
  share_class_figi?: string | null;
  sic_code?: string | null;
  sic_description?: string | null;
  type?: string | null;
  currency_name?: string | null;
  locale?: string | null;
  market?: string | null;
  share_class_shares_outstanding?: number | null;
  weighted_shares_outstanding?: number | null;
  round_lot?: number | null;
  active?: boolean | null;
  delisted_utc?: string | null;
  address1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  overview_source?: OverviewSource | null;
}

export interface FirmList {
  count: number;
  items: FirmSummary[];
}

export interface Stats {
  total: number;
  by_status: { queued: number; in_progress: number; done: number };
  sectors: number;
  combined_revenue_b_usd: number;
  firms_with_financials: number;
  latest_pulled_at: string | null;
}

async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`);
  if (!res.ok) {
    throw new Error(`GET ${path} failed: ${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export interface SectorTopFirm {
  slug: string;
  name: string;
  ticker: string | null;
}

export interface SectorSummary {
  name: string;
  firm_count: number;
  combined_revenue_b_usd: number;
  combined_market_cap_b_usd: number;
  modeled: number;
  queued: number;
  in_progress: number;
  top_firms: SectorTopFirm[];
}

export const fetchFirms = () => getJson<FirmList>("/firms");
export const fetchFirm = (slug: string) =>
  getJson<Firm>(`/firms/${encodeURIComponent(slug)}`);
export const fetchSectors = () =>
  getJson<{ items: string[] }>("/sectors").then((r) => r.items);
export const fetchSectorsSummary = () =>
  getJson<{ items: SectorSummary[] }>("/sectors/summary").then((r) => r.items);
export const fetchStats = () => getJson<Stats>("/stats");

// The signed-in buyer's own access token, for the /api page's pre-filled agent
// block. Same-origin so the session cookie rides along. Returns null when not
// signed in (401) or for admin sessions with no buyer token (404), so the page
// falls back to the YOUR_TOKEN placeholder and never ships a real token to anon
// visitors.
export async function fetchMyToken(): Promise<string | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/token`, { credentials: "same-origin" });
    if (!res.ok) return null;
    const json = (await res.json()) as { display_token?: string | null };
    return json.display_token ?? null;
  } catch {
    return null;
  }
}

export interface HeatmapFirm {
  slug: string;
  name: string;
  ticker: string | null;
  industry: string | null;
  market_cap_b_usd: number;
  revenue_b_usd: number;
  operating_margin: number | null;
}

export interface HeatmapSector {
  name: string;
  market_cap_b_usd: number;
  revenue_b_usd: number;
  firms: HeatmapFirm[];
}

export const fetchHeatmap = () =>
  getJson<{ items: HeatmapSector[] }>("/heatmap").then((r) => r.items);

export interface PeerRow {
  slug: string;
  name: string;
  ticker: string | null;
  is_anchor: boolean;
  fiscal_year: number | null;
  revenue: number | null;
  operating_income: number | null;
  operating_margin: number | null;
  net_income: number | null;
  free_cash_flow: number | null;
  market_cap: number | null;
  employees: number | null;
  has_logo: boolean;
  has_icon: boolean;
}

export interface PeerMedian {
  revenue: number | null;
  operating_margin: number | null;
  free_cash_flow: number | null;
  market_cap: number | null;
}

export interface PeerSet {
  sector: string | null;
  items: PeerRow[];
  median: PeerMedian;
}

export const fetchPeers = (slug: string, limit = 6) =>
  getJson<PeerSet>(`/firms/${encodeURIComponent(slug)}/peers?limit=${limit}`);
